/**
 * logger.js — module-level console ring-buffer + production silencing.
 *
 * Patches console.log/warn/error exactly ONCE at module-load time so that
 * ALL app code (App.js, hooks, utils) is captured without any per-render
 * monkey-patching inside React lifecycle methods.
 *
 * Usage:
 *   import { getBuffer } from './utils/logger';
 *   // In debug dump: getBuffer().slice(-60)
 *
 * The module is side-effect-only — just importing it activates the patching.
 * Import it as early as possible (src/index.js) so no log is missed.
 */

const BUFFER_SIZE = 150;
const _buffer = [];

const _serialize = (args) =>
  args
    .map(a => {
      try { return typeof a === 'object' ? JSON.stringify(a) : String(a); }
      catch { return '[unserializable]'; }
    })
    .join(' ');

// Save originals BEFORE any override so forwarding always reaches the real console
const _orig = {
  debug: console.debug.bind(console), // eslint-disable-line no-console
  log:   console.log.bind(console),   // eslint-disable-line no-console
  warn:  console.warn.bind(console),  // eslint-disable-line no-console
  error: console.error.bind(console), // eslint-disable-line no-console
};

const _capture = (level) => (...args) => {
  // Suppress log/info in production so the end-user console stays clean
  if (level !== 'log' || import.meta.env.MODE !== 'production') {
    _orig[level](...args);
  }
  _buffer.push({ t: Date.now(), level, msg: _serialize(args) });
  if (_buffer.length > BUFFER_SIZE) _buffer.shift();
};

// ── Patch globals once, at module-load time ──────────────────────────────────
/* eslint-disable no-console */
console.log   = _capture('log');
console.warn  = _capture('warn');
console.error = _capture('error');

// Also silence debug/info in production (not in ring buffer, but shouldn't clutter)
if (import.meta.env.MODE === 'production') {
  console.debug = console.info = () => {};
}
/* eslint-enable no-console */

/**
 * Emits development-only diagnostics without adding noise to the production buffer.
 */
export const debug = (...args) => {
  if (import.meta.env.DEV) _orig.debug(...args);
};

/**
 * Returns a defensive copy of the ring buffer.
 * @returns {Array<{t: number, level: string, msg: string}>}
 */
export const getBuffer = () => _buffer.slice();
